import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { SelectChangeEvent } from "@mui/material";

import { CustomButton, IButtonVariant } from "../../components/CustomButton";
import { CustomModal } from "../../components/CustomModal";
import { resetBillingList } from "../../middleware/listenerMiddleware/utils/resetBillingsList";
import { postCreateBillingThunk } from "../../reducers/billingReducer";
import { getBillingsCreateAvailableYears } from "../../selectors/billings";
import { useAppDispatch } from "../../store";
import { BillingType } from "../../types/billings";
import { IModal } from "../../types/modal";
import { buildBillingPageUrl } from "../../utils/url";
import { BillingTypeCardSelector } from "../BillingTypeCardSelector";
import { SelectComponent } from "../SelectComponent";

import styles from "./NewBillingModal.module.scss";

type NewBillingModal = IModal;

export const NewBillingModal: React.FC<NewBillingModal> = ({ isModalVisible, toggleModalVisibility }) => {
  const { t } = useTranslation();
  const availableYears = useSelector(getBillingsCreateAvailableYears);
  const dispatch = useAppDispatch();
  const history = useHistory();
  const [error, setError] = useState("");
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [selectedYear, setSelectedYear] = useState<number>(availableYears[0]);
  const [selectedBillingType, setSelectedBillingType] = useState<string>(BillingType.Weight);

  const yearsOptions = useMemo(
    () => availableYears.map((year) => ({ value: year, label: String(year) })),
    [availableYears]
  );

  const handleCreateBilling = async () => {
    setIsFetching(true);

    const result = await dispatch(postCreateBillingThunk({ year: selectedYear }));

    if (postCreateBillingThunk.fulfilled.match(result)) {
      const billingId = result.payload.billingId;

      resetBillingList(dispatch);
      history.push(buildBillingPageUrl(billingId));

      toggleModalVisibility();
    } else if (postCreateBillingThunk.rejected.match(result)) {
      setError(t(`billingApiErrors.${result.payload}`));
    }

    setIsFetching(false);
  };

  const clearModalState = () => {
    setError("");
    setSelectedYear(availableYears[0]);
    setSelectedBillingType(BillingType.Weight);
  };

  const handleCloseModal = () => {
    clearModalState();
    toggleModalVisibility();
  };

  const handleChange = (event: SelectChangeEvent): void => {
    setSelectedYear(Number(event.target.value));
  };

  return (
    <CustomModal
      isModalVisible={isModalVisible}
      toggleModalVisibility={handleCloseModal}
      title={t("newBillingModal.title")}
      text={t("newBillingModal.label")}
    >
      <SelectComponent
        label={t("newBillingModal.period")}
        value={selectedYear}
        handleChange={handleChange}
        optionsArray={yearsOptions}
      />
      <BillingTypeCardSelector selectedId={selectedBillingType} onChange={setSelectedBillingType} />
      {error && <div className={styles.error}>{error}</div>}
      <div className={styles.formContainer}>
        <CustomButton
          text={t("newBillingModal.cancel")}
          onClick={handleCloseModal}
          disabled={isFetching}
          variant={IButtonVariant.neutral}
        />
        <CustomButton text={t("newBillingModal.create")} onClick={handleCreateBilling} disabled={isFetching} />
      </div>
    </CustomModal>
  );
};
